/**
 * Чистые функции мастера подключения Telegram-аккаунтов (KAN-601).
 * Без React и I/O: валидация, тексты ошибок API, время ожидания, multipart.
 */
import { isOperatorRequiredError, OPERATOR_REQUIRED_TEXT } from '@/lib/channelOperators';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { parseRubAmount, validateRubAmount } from '@/lib/money';
import {
  EMPTY_PROXY_FORM,
  validateOptionalProxy,
  type ProxyFormValues,
} from '@/lib/telegramProxy';
import type {
  ArchiveDetected,
  ArchiveInspectDuplicate,
  ArchiveInspectFormat,
  ConnectScheme,
  LoginStartBody,
} from '@/types/telegramAccount';

/** Лимиты загрузки по контракту (docs/gateway/commands-v1.md, раздел 2). */
export const MAX_SESSION_FILE_BYTES = 1024 * 1024;
export const MAX_SELLER_JSON_BYTES = 64 * 1024;
/** KAN-677: zip от продавца (как MAX_TDATA_ZIP_BYTES на сервере, общий для tdata и сессии). */
export const MAX_ARCHIVE_ZIP_BYTES = 50 * 1024 * 1024;

const FALLBACK_FLOOD_SECONDS = 60;

/** Подпись способа подключения: вкладки мастера и итоговый шаг. */
export const SCHEME_LABELS: Record<ConnectScheme, string> = {
  code_2fa: 'Код и пароль 2FA',
  code: 'Только код',
  archive: 'Архив от продавца (zip)',
  session: 'Сессия вручную',
};

/** KAN-677: подпись формата, который сервер нашёл в архиве (итоговый шаг). */
export const DETECTED_LABELS: Record<ArchiveDetected, string> = {
  tdata: 'tdata',
  session: 'сессия Telethon',
};

/** KAN-722: подпись формата из разбора архива (archive/inspect/). */
export const ARCHIVE_FORMAT_LABELS: Record<ArchiveInspectFormat, string> = {
  tdata: 'tdata',
  telethon: 'Telethon (session+json)',
  pyrogram: 'Pyrogram',
};

/** Метка аккаунта: как в инлайн-редакторе (KAN-625), 1..64 символа. */
export const LABEL_MAX = 64;

/**
 * KAN-722: как задаётся метка: none - без метки, prefix - сервер назначает
 * следующую по префиксу (label_prefix), custom - своя метка текстом (label).
 */
export type LabelMode = 'none' | 'prefix' | 'custom';

/**
 * Общие поля мастера: метка, прокси, флаги (выбираются до входа).
 * KAN-655: прокси в форме полями (как в диалоге смены прокси), в API уходит
 * строкой URL через proxyToUrl; пустые поля = без прокси.
 * KAN-670: sellerId - карточка продавца из справочника ("" - не указан).
 * KAN-713: proxyId - прокси из справочника ("" - поля вводятся вручную).
 */
export interface CommonValues {
  labelMode: LabelMode;
  /** Код префикса (labelMode=prefix). */
  labelPrefix: string;
  /** Своя метка (labelMode=custom). */
  label: string;
  /** Метку выбрал пользователь: автоподбор по номеру её больше не меняет. */
  labelTouched: boolean;
  proxy: ProxyFormValues;
  proxyId: string;
  enabled: boolean;
  outreach: boolean;
  sellerId: string;
  /** KAN-735: стоимость покупки, ₽, текстом поля ("" - не указана). */
  purchaseCost: string;
}

/** KAN-722: по умолчанию аккаунт включён и первым не пишет (решение владельца). */
export const DEFAULT_COMMON: CommonValues = {
  labelMode: 'none',
  labelPrefix: '',
  label: '',
  labelTouched: false,
  proxy: EMPTY_PROXY_FORM,
  proxyId: '',
  enabled: true,
  outreach: false,
  sellerId: '',
  purchaseCost: '',
};

/** KAN-722: поля метки для тела запроса: своя метка ИЛИ код префикса. */
export function resolveLabel(common: CommonValues): {
  label: string;
  labelPrefix: string;
} {
  if (common.labelMode === 'custom') return { label: common.label, labelPrefix: '' };
  if (common.labelMode === 'prefix') return { label: '', labelPrefix: common.labelPrefix };
  return { label: '', labelPrefix: '' };
}

/**
 * KAN-722: автоподбор префикса (по номеру или из разбора архива). Не трогает
 * выбор пользователя; без подобранного кода возвращает "Без метки".
 */
export function applyLabelSuggestion(
  common: CommonValues,
  code: string | null | undefined
): CommonValues {
  if (common.labelTouched) return common;
  return code
    ? { ...common, labelMode: 'prefix', labelPrefix: code }
    : { ...common, labelMode: 'none', labelPrefix: '' };
}

/**
 * KAN-739: текст блокировки, если номер из архива уже в кабинете.
 * null - дубля нет, подключать можно.
 */
export function describeInspectDuplicate(
  duplicate: ArchiveInspectDuplicate | null | undefined
): string | null {
  if (!duplicate) return null;
  if (duplicate.decommissioned) {
    return `Номер есть в кабинете как списанный ${duplicate.label}. Верните его кнопкой Вернуть в списке аккаунтов.`;
  }
  return `Этот номер уже подключён: ${duplicate.label}. Повторное подключение убьёт сессию.`;
}

/**
 * KAN-722: метка из разбора архива: подобранный префикс, иначе подобранная
 * метка как "Своя метка"; выбор пользователя не трогается.
 */
export function applyArchiveInspect(
  common: CommonValues,
  inspect: { suggested_prefix: string | null; suggested_label: string | null }
): CommonValues {
  if (common.labelTouched) return common;
  if (!inspect.suggested_prefix && inspect.suggested_label) {
    return { ...common, labelMode: 'custom', label: inspect.suggested_label, labelPrefix: '' };
  }
  return applyLabelSuggestion(common, inspect.suggested_prefix);
}

/** KAN-722: "998, +7 ,," -> ["998", "+7"]. */
export function parsePhonePrefixes(raw: string): string[] {
  return raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Галочки схем импорта (архив от продавца, сессия вручную). KAN-676: проверка @SpamBot
 * по умолчанию выключена (запрос к @SpamBot сразу после входа отзывал свежие
 * сессии, KAN-661). KAN-648: had2fa не отмечено - признак не передаётся.
 */
export interface ImportFlags {
  checkSpam: boolean;
  had2fa: boolean;
}

export const DEFAULT_IMPORT_FLAGS: ImportFlags = {
  checkSpam: false,
  had2fa: false,
};

/** KAN-713: прокси из справочника не проверяется формой; поля - как раньше. */
export function validateCommonProxy(common: CommonValues): string | null {
  return common.proxyId ? null : validateOptionalProxy(common.proxy);
}

/** KAN-735: ошибка поля "Стоимость покупки" или null (пусто допустимо). */
export function validatePurchaseCost(raw: string): string | null {
  return validateRubAmount(raw);
}

/** KAN-735: стоимость покупки числом или undefined (пусто или не число - не отправляется). */
function purchaseCostNumber(raw: string | undefined): number | undefined {
  return parseRubAmount(raw ?? '') ?? undefined;
}

/** KAN-713: id прокси из справочника числом или undefined. */
function proxyIdNumber(proxyId: string | undefined): number | undefined {
  const n = Number(proxyId);
  return proxyId && Number.isInteger(n) && n > 0 ? n : undefined;
}

// ---------------------------------------------------------------- телефон

/**
 * Убирает пробелы, дефисы и скобки; 8XXXXXXXXXX приводит к +7XXXXXXXXXX;
 * международный номер из 11-15 цифр без плюса и без ведущего 0 получает "+".
 */
export function normalizePhone(raw: string): string {
  const cleaned = (raw ?? '').replace(/[\s\-().]/g, '');
  if (/^8\d{10}$/.test(cleaned)) return `+7${cleaned.slice(1)}`;
  if (/^[1-9]\d{10,14}$/.test(cleaned)) return `+${cleaned}`;
  return cleaned;
}

/** Текст ошибки или null, если номер корректен (формат +7XXXXXXXXXX и международный). */
export function validatePhone(raw: string): string | null {
  const phone = normalizePhone(raw);
  if (!phone) return 'Введите номер телефона';
  if (!/^\+\d{10,15}$/.test(phone)) {
    return 'Номер в формате +79001234567';
  }
  return null;
}

// ------------------------------------------------------------------ файлы

/** Размер строки в байтах UTF-8. */
export function textBytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

function formatLimit(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${bytes / (1024 * 1024)} МБ`
    : `${bytes / 1024} КБ`;
}

/** Проверка файла .session Telethon: расширение, не пустой, до 1 МБ. */
export function validateSessionFile(file: {
  name: string;
  size: number;
}): string | null {
  if (!/\.session$/i.test(file.name)) {
    return 'Нужен файл с расширением .session';
  }
  if (file.size === 0) return 'Файл пустой';
  if (file.size > MAX_SESSION_FILE_BYTES) {
    return `Файл больше ${formatLimit(MAX_SESSION_FILE_BYTES)}`;
  }
  return null;
}

/** Ищет ключ без учёта регистра среди вариантов имён. */
function hasAnyKey(obj: Record<string, unknown>, names: string[]): boolean {
  const keys = Object.keys(obj).map((k) => k.toLowerCase());
  return names.some((n) => keys.includes(n));
}

/**
 * Проверка JSON продавца (файл или текст): размер до 64 КБ, валидный JSON-объект
 * с ключами приложения Telegram (api_id|app_id и api_hash|app_hash).
 */
export function validateSellerJson(
  text: string,
  sizeBytes: number = textBytes(text)
): string | null {
  if (sizeBytes > MAX_SELLER_JSON_BYTES) {
    return `JSON больше ${formatLimit(MAX_SELLER_JSON_BYTES)}`;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return 'JSON продавца некорректен';
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'JSON продавца должен быть объектом';
  }
  const obj = parsed as Record<string, unknown>;
  if (!hasAnyKey(obj, ['api_id', 'app_id'])) {
    return 'В JSON продавца нет api_id (app_id)';
  }
  if (!hasAnyKey(obj, ['api_hash', 'app_hash'])) {
    return 'В JSON продавца нет api_hash (app_hash)';
  }
  return null;
}

/** Размер JSON-файла проверяем до чтения содержимого. */
export function validateSellerJsonFileSize(size: number): string | null {
  if (size > MAX_SELLER_JSON_BYTES) {
    return `JSON больше ${formatLimit(MAX_SELLER_JSON_BYTES)}`;
  }
  return null;
}

/** KAN-677: zip от продавца: расширение .zip, не пустой, до 50 МБ. Содержимое проверяет сервер. */
export function validateArchiveZip(file: {
  name: string;
  size: number;
}): string | null {
  if (!/\.zip$/i.test(file.name)) {
    return 'Нужен файл с расширением .zip';
  }
  if (file.size === 0) return 'Файл пустой';
  if (file.size > MAX_ARCHIVE_ZIP_BYTES) {
    return `Архив больше ${formatLimit(MAX_ARCHIVE_ZIP_BYTES)}`;
  }
  return null;
}

// ------------------------------------------------------------- ожидание

/** 90 -> "2 мин", 30 -> "30 сек". */
export function formatWait(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s < 60) return `${s} сек`;
  return `${Math.ceil(s / 60)} мин`;
}

/** Текст 429: "Повторите через N мин". */
export function floodWaitMessage(seconds: number): string {
  return `Повторите через ${formatWait(seconds)}`;
}

interface AxiosLike {
  response?: {
    status?: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
}

function statusOf(err: unknown): number | undefined {
  return (err as AxiosLike | null)?.response?.status;
}

/** Секунды ожидания из 429: поле retry_after, затем заголовок Retry-After. */
export function getRetryAfterSeconds(err: unknown): number | null {
  if (statusOf(err) !== 429) return null;
  const res = (err as AxiosLike).response;
  const data = res?.data as { retry_after?: unknown } | undefined;
  const fromBody = Number(data?.retry_after);
  if (Number.isFinite(fromBody) && fromBody > 0) return fromBody;
  const header = Number(res?.headers?.['retry-after']);
  if (Number.isFinite(header) && header > 0) return header;
  return FALLBACK_FLOOD_SECONDS;
}

/** Понятный текст ошибки API мастера. */
export function describeApiError(err: unknown, fallback: string): string {
  const flood = getRetryAfterSeconds(err);
  if (flood !== null) return floodWaitMessage(flood);
  // KAN-752: несколько кампаний Telegram, а кампания не выбрана
  if (isOperatorRequiredError(err)) return OPERATOR_REQUIRED_TEXT;
  const status = statusOf(err);
  if (status === 503) return 'Держатель сессий недоступен';
  // KAN-776: nginx 413 на партии архивов (лимит 210 МБ)
  if (status === 413) return 'Слишком большой запрос, уменьшите число архивов';
  if (status === 504) {
    return 'Держатель сессий не ответил вовремя. Проверьте состояние аккаунта перед повтором';
  }
  // 409 и 4xx: текст сервера на русском
  return extractErrorMessage(err, fallback);
}

/**
 * Текст ошибки обновления профиля: при 503 сервер объясняет причину
 * (профиль появится после перезапуска держателя или контейнера), её и показываем.
 * Общий текст 503 только если сервер текста не прислал.
 */
export function describeProfileRefreshError(
  err: unknown,
  fallback: string
): string {
  if (statusOf(err) === 503) {
    const data = (err as AxiosLike).response?.data as
      | { detail?: unknown }
      | undefined;
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return describeApiError(err, fallback);
}

// --------------------------------------------------------- тела запросов

/**
 * KAN-676: галочка в формах импорта называется "Проверять @SpamBot" и по
 * умолчанию выключена; в API поле обратное: skip_spam_check=true, пока
 * проверка не включена.
 */
export function skipSpamCheckFromFlag(checkSpam: boolean): boolean {
  return !checkSpam;
}

export interface CodeFlowFields {
  phone: string;
  label: string;
  /** KAN-722: код префикса; label пустой - метку назначает сервер. */
  labelPrefix?: string;
  proxy: string;
  enabled: boolean;
  outreach: boolean;
  relogin?: { accountId: string; operatorId?: string };
  operatorId?: string;
  /** KAN-670: id карточки продавца (KAN-673: ссылка на заказ не отправляется). */
  sellerId?: string;
  /** KAN-713: прокси из справочника; задан - proxy не отправляется. */
  proxyId?: string;
  /** KAN-735: стоимость покупки текстом поля; пусто - не отправляется. */
  purchaseCost?: string;
}

/** Тело POST login/start/ без пустых необязательных полей. */
export function buildLoginStartBody(f: CodeFlowFields): LoginStartBody {
  const body: LoginStartBody = {
    phone: normalizePhone(f.phone),
    intent: f.relogin ? 'relogin' : 'new',
    enabled: f.enabled,
    outreach: f.outreach,
  };
  if (f.relogin) body.account_id = f.relogin.accountId;
  if (f.label.trim()) body.label = f.label.trim();
  else if (f.labelPrefix?.trim()) body.label_prefix = f.labelPrefix.trim();
  const proxyId = proxyIdNumber(f.proxyId);
  if (proxyId) body.proxy_id = proxyId;
  else if (f.proxy.trim()) body.proxy = f.proxy.trim();
  if (f.sellerId?.trim()) body.seller_id = f.sellerId.trim();
  const cost = purchaseCostNumber(f.purchaseCost);
  if (cost !== undefined) body.purchase_cost_rub = cost;
  const operatorId = f.relogin?.operatorId ?? f.operatorId;
  if (operatorId) body.operator_id = operatorId;
  return body;
}

export interface ImportFields {
  sessionFile: File | null;
  sessionString: string;
  sellerJsonFile: File | null;
  sellerJsonText: string;
  label: string;
  /** KAN-722: код префикса метки. */
  labelPrefix?: string;
  proxy: string;
  enabled: boolean;
  outreach: boolean;
  skipSpamCheck: boolean;
  operatorId?: string;
  /** KAN-670: id карточки продавца (KAN-673: ссылка на заказ не отправляется). */
  sellerId?: string;
  /** KAN-648: галочка "был 2FA"; не отмечена: признак не передаётся (неизвестно). */
  had2fa?: boolean;
  /** KAN-713: прокси из справочника; задан - proxy не отправляется. */
  proxyId?: string;
  /** KAN-735: стоимость покупки текстом поля. */
  purchaseCost?: string;
}

/** KAN-722: метка в multipart: своя метка или код префикса. */
export function appendLabel(fd: FormData, label: string, labelPrefix: string | undefined): void {
  if (label.trim()) fd.append('label', label.trim());
  else if (labelPrefix?.trim()) fd.append('label_prefix', labelPrefix.trim());
}

/** Прокси в multipart: proxy_id из справочника или строка URL. */
function appendProxy(fd: FormData, proxy: string, proxyId: string | undefined): void {
  const id = proxyIdNumber(proxyId);
  if (id) fd.append('proxy_id', String(id));
  else if (proxy.trim()) fd.append('proxy', proxy.trim());
}

/** KAN-735: стоимость покупки в multipart, пусто - не отправляется. */
function appendPurchaseCost(fd: FormData, raw: string | undefined): void {
  const cost = purchaseCostNumber(raw);
  if (cost !== undefined) fd.append('purchase_cost_rub', String(cost));
}

/**
 * Общие поля импорта (сессия, архив, партия архивов KAN-776) в multipart:
 * enabled, outreach, seller_id, skip_spam_check, has_2fa_at_creation,
 * purchase_cost_rub, operator_id. Пустые необязательные не отправляются.
 */
export interface ImportCommonFields {
  enabled: boolean;
  outreach: boolean;
  skipSpamCheck: boolean;
  sellerId?: string;
  had2fa?: boolean;
  purchaseCost?: string;
  operatorId?: string;
}

export function appendImportCommonFields(fd: FormData, f: ImportCommonFields): void {
  fd.append('enabled', String(f.enabled));
  fd.append('outreach', String(f.outreach));
  if (f.sellerId?.trim()) fd.append('seller_id', f.sellerId.trim());
  if (f.skipSpamCheck) fd.append('skip_spam_check', 'true');
  if (f.had2fa) fd.append('has_2fa_at_creation', 'true');
  appendPurchaseCost(fd, f.purchaseCost);
  if (f.operatorId) fd.append('operator_id', f.operatorId);
}

/** multipart для POST import/: файл ИЛИ текст для сессии и для JSON продавца. */
export function buildImportFormData(f: ImportFields): FormData {
  const fd = new FormData();
  if (f.sessionFile) fd.append('session_file', f.sessionFile);
  else fd.append('session_string', f.sessionString.trim());
  if (f.sellerJsonFile) fd.append('seller_json_file', f.sellerJsonFile);
  else if (f.sellerJsonText.trim()) {
    fd.append('seller_json', f.sellerJsonText.trim());
  }
  appendLabel(fd, f.label, f.labelPrefix);
  appendProxy(fd, f.proxy, f.proxyId);
  appendImportCommonFields(fd, f);
  return fd;
}

/** KAN-677: поля формы "Архив от продавца (zip)": архив вместо сессии и JSON продавца. */
export interface ImportArchiveFields {
  archive: File;
  label: string;
  /** KAN-722: код префикса метки. */
  labelPrefix?: string;
  proxy: string;
  enabled: boolean;
  outreach: boolean;
  skipSpamCheck: boolean;
  operatorId?: string;
  sellerId?: string;
  had2fa?: boolean;
  /** KAN-713: прокси из справочника. */
  proxyId?: string;
  /** KAN-735: стоимость покупки текстом поля. */
  purchaseCost?: string;
}

/** multipart для POST import-archive/: zip и те же общие поля, что у импорта сессии. */
export function buildImportArchiveFormData(f: ImportArchiveFields): FormData {
  const fd = new FormData();
  fd.append('archive', f.archive);
  appendLabel(fd, f.label, f.labelPrefix);
  appendProxy(fd, f.proxy, f.proxyId);
  appendImportCommonFields(fd, f);
  return fd;
}

/** KAN-722: multipart для POST archive/inspect/: тот же файл, что у import-archive/. */
export function buildArchiveInspectFormData(archive: File, operatorId?: string): FormData {
  const fd = new FormData();
  fd.append('archive', archive);
  // KAN-752: кампания выбрана в мастере (несколько кампаний); иначе не передаём
  if (operatorId) fd.append('operator_id', operatorId);
  return fd;
}
